import { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  Tabs,
  Tab,
  Typography,
  TextField,
  Stack,
  FormGroup,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Button,
  Divider,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import * as materialApi from "@/api/material";
import * as patientApi from "@/api/patient";
import type { MaterialResponseDTO } from "@/api/types";
import type { PatientFormData } from "@/types/PatientFormData";
import { getEmptyFormData } from "@/utils/patientMapper";
import PatientFehlerTab from "@/components/PatientFehlerTab";
import type { PatientEditModalProps } from "./PatientEditModal";

const PatientEditModal2024 = ({
  open,
  onClose,
  onSave,
  editingPatient,
  editingFormData,
}: PatientEditModalProps) => {
  const patientId = editingPatient?.id;

  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState<PatientFormData>({
    ...getEmptyFormData(),
    ...(editingFormData || {}),
  });
  const [materials, setMaterials] = useState<MaterialResponseDTO[]>([]);
  const [hasErrorsOrHints, setHasErrorsOrHints] = useState(false);

  useEffect(() => {
    if (open) {
      setFormData({
        ...getEmptyFormData(),
        ...(editingFormData || {}),
      });
      setActiveTab(0);
    }
  }, [open, editingFormData]);

  useEffect(() => {
    let isMounted = true;
    materialApi
      .getAll()
      .then((data) => {
        if (!isMounted) return;
        setMaterials(data);
      })
      .catch(() => {
        if (!isMounted) return;
        setMaterials([]);
      });

    if (patientId) {
      patientApi
        .getFehler(patientId)
        .then((data) => {
          if (!isMounted) return;
          setHasErrorsOrHints(Array.isArray(data) && data.length > 0);
        })
        .catch(() => {
          if (!isMounted) return;
          setHasErrorsOrHints(false);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [patientId]);

  const handleCheckboxChange = (field: keyof PatientFormData) => {
    setFormData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const handleTextChange = (field: keyof PatientFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const checkboxGroupSx = {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 1,
  };

  const checkboxItemSx = {
    m: 0,
    flex: "0 1 240px",
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "1200px",
          maxWidth: "95vw",
          height: "80vh",
          maxHeight: "800px",
        },
      }}
    >
      <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
        <Typography variant="h5" component="h2" gutterBottom sx={{ flexShrink: 0 }}>
          {editingPatient
            ? `Patientin bearbeiten (2024)`
            : `Neue Patientin hinzufügen (2024)`}
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              minHeight: 0,
              "& .MuiFormControlLabel-label": {
                fontSize: "0.875rem",
              },
              "& .MuiTypography-h6": {
                fontSize: "1rem",
              },
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: "divider", mb: 3, flexShrink: 0 }}
            >
              <Tab label="Stammdaten" />
              <Tab label="OP-Therapie" />
              <Tab label="OP-Technik" />
              <Tab label="OP-Material" />
              <Tab label="OP-Komplikationen" />
              <Tab label="Kontrolle 3M" />
              <Tab label="Kontrolle 12M" />
              {hasErrorsOrHints && <Tab label="Fehler / Hinweise" />}
            </Tabs>

            <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", mb: 2, pt: 1, minHeight: 0 }}>
              {/* Tab 1: Stammdaten */}
              {activeTab === 0 && (
                <Box>
                  <Stack spacing={3}>
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                      <TextField
                        label="Patientenidentifikation"
                        value={formData.patientId}
                        onChange={(e) => handleTextChange("patientId", e.target.value)}
                        fullWidth
                        required
                      />
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Altersklasse
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.altersklassenKl30 || false}
                              onChange={() => handleCheckboxChange("altersklassenKl30")}
                            />
                          }
                          label="< 30 Jahre"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.altersklassen30_49 || false}
                              onChange={() => handleCheckboxChange("altersklassen30_49")}
                            />
                          }
                          label="30-49 Jahre"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.altersklassen50_59 || false}
                              onChange={() => handleCheckboxChange("altersklassen50_59")}
                            />
                          }
                          label="50-59 Jahre"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.altersklassen60_69 || false}
                              onChange={() => handleCheckboxChange("altersklassen60_69")}
                            />
                          }
                          label="60-69 Jahre"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.altersklassen70_80 || false}
                              onChange={() => handleCheckboxChange("altersklassen70_80")}
                            />
                          }
                          label="70-80 Jahre"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.altersklassenGr80 || false}
                              onChange={() => handleCheckboxChange("altersklassenGr80")}
                            />
                          }
                          label="> 80 Jahre"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Diagnose
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.diagnoseHarninkontinenz || false}
                              onChange={() => handleCheckboxChange("diagnoseHarninkontinenz")}
                            />
                          }
                          label="Harninkontinenz"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.diagnoseSenkung || false}
                              onChange={() => handleCheckboxChange("diagnoseSenkung")}
                            />
                          }
                          label="Senkung"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.diagnoseKombination || false}
                              onChange={() => handleCheckboxChange("diagnoseKombination")}
                            />
                          }
                          label="Kombination"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Präoperative konservative Therapie
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.praeopKonsTherapieHormontherapie || false}
                              onChange={() => handleCheckboxChange("praeopKonsTherapieHormontherapie")}
                            />
                          }
                          label="Hormontherapie"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.praeopKonsTherapiePhysiotherapie || false}
                              onChange={() => handleCheckboxChange("praeopKonsTherapiePhysiotherapie")}
                            />
                          }
                          label="Physiotherapie"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* Tab 2: OP-Therapie */}
              {activeTab === 1 && (
                <Box>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Art des Eingriffs
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.opTherapiePrimaereingriffHik || false}
                              onChange={() => handleCheckboxChange("opTherapiePrimaereingriffHik")}
                            />
                          }
                          label="Primäreingriff Harninkontinenz (HIK)"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.opTherapiePrimaereingriffSenkung || false}
                              onChange={() => handleCheckboxChange("opTherapiePrimaereingriffSenkung")}
                            />
                          }
                          label="Primäreingriff Senkung"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.opTherapieRezidiveingriffHik || false}
                              onChange={() => handleCheckboxChange("opTherapieRezidiveingriffHik")}
                            />
                          }
                          label="Rezidiveingriff Harninkontinenz (HIK)"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.opTherapieRezidiveingriffSenkung || false}
                              onChange={() => handleCheckboxChange("opTherapieRezidiveingriffSenkung")}
                            />
                          }
                          label="Rezidiveingriff Senkung"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.opTherapieWeitere || false}
                              onChange={() => handleCheckboxChange("opTherapieWeitere")}
                            />
                          }
                          label="Weitere"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* Tab 3: OP-Technik */}
              {activeTab === 2 && (
                <Box>
                  <Stack spacing={3}>
                    <Box sx={{ pt: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Vaginale Senkungsoperationen
                      </Typography>
                      <Box sx={{ pl: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 1 }}>
                          ohne allopl. Material
                        </Typography>
                        <FormGroup row sx={checkboxGroupSx}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.optechnikAb2023VagsenkOhne || false}
                                onChange={() => handleCheckboxChange("optechnikAb2023VagsenkOhne")}
                              />
                            }
                            label="ohne allopl. Material"
                            sx={checkboxItemSx}
                          />
                        </FormGroup>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 1 }}>
                          mit allopl. Material
                        </Typography>
                        <FormGroup row sx={checkboxGroupSx}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.optechnikAb2023VagsenkVorderesnetzMitfixierung || false}
                                onChange={() => handleCheckboxChange("optechnikAb2023VagsenkVorderesnetzMitfixierung")}
                              />
                            }
                            label="vorderes Netz mit Fixierung"
                            sx={checkboxItemSx}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.optechnikAb2023VagsenkVorderesnetzOhnefixierung || false}
                                onChange={() => handleCheckboxChange("optechnikAb2023VagsenkVorderesnetzOhnefixierung")}
                              />
                            }
                            label="vorderes Netz ohne Fixierung"
                            sx={checkboxItemSx}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.optechnikAb2023VagsenkHinteresnetz || false}
                                onChange={() => handleCheckboxChange("optechnikAb2023VagsenkHinteresnetz")}
                              />
                            }
                            label="hinteres Netz"
                            sx={checkboxItemSx}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.optechnikAb2023VagsenkKombiniertesnetz || false}
                                onChange={() => handleCheckboxChange("optechnikAb2023VagsenkKombiniertesnetz")}
                              />
                            }
                            label="kombiniertes Netz"
                            sx={checkboxItemSx}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.optechnikAb2023VagsenkApikalesband || false}
                                onChange={() => handleCheckboxChange("optechnikAb2023VagsenkApikalesband")}
                              />
                            }
                            label="apikales Band (z.B. BSC, Splentis)"
                            sx={checkboxItemSx}
                          />
                        </FormGroup>
                      </Box>
                    </Box>

                    <Divider />

                    <Box sx={{ pt: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Abdominale Senkungsoperationen
                      </Typography>
                      <Box sx={{ pl: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 1 }}>
                          ohne allopl. Material
                        </Typography>
                        <FormGroup row sx={checkboxGroupSx}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.optechnikAb2023AbdsenkOhne || false}
                                onChange={() => handleCheckboxChange("optechnikAb2023AbdsenkOhne")}
                              />
                            }
                            label="ohne allopl. Material"
                            sx={checkboxItemSx}
                          />
                        </FormGroup>
                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 1 }}>
                          mit allopl. Material
                        </Typography>
                        <FormGroup row sx={checkboxGroupSx}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.optechnikAb2023AbdsenkSacropexie || false}
                                onChange={() => handleCheckboxChange("optechnikAb2023AbdsenkSacropexie")}
                              />
                            }
                            label="Sacropexie mit Netz an der Vagina/Cervix/Uterus"
                            sx={checkboxItemSx}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.optechnikAb2023AbdsenkPectopecie || false}
                                onChange={() => handleCheckboxChange("optechnikAb2023AbdsenkPectopecie")}
                              />
                            }
                            label="Pectopexie mit Netz an der Vagina/Cervix/Uterus"
                            sx={checkboxItemSx}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formData.optechnikAb2023AbdsenkAndere || false}
                                onChange={() => handleCheckboxChange("optechnikAb2023AbdsenkAndere")}
                              />
                            }
                            label="andere abdominale Netzeinlage bei kombinierten Senkungen"
                            sx={checkboxItemSx}
                          />
                        </FormGroup>
                      </Box>
                    </Box>

                    <Divider />

                    <Box sx={{ pt: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Inkontinenz Operationen
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.optechnikAb2023InkopTvtretro || false}
                              onChange={() => handleCheckboxChange("optechnikAb2023InkopTvtretro")}
                            />
                          }
                          label="TVT retropubisch"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.optechnikAb2023InkopTvtobtu || false}
                              onChange={() => handleCheckboxChange("optechnikAb2023InkopTvtobtu")}
                            />
                          }
                          label="TVT obturatorisch (TOT)"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.optechnikAb2023InkopTvtmini || false}
                              onChange={() => handleCheckboxChange("optechnikAb2023InkopTvtmini")}
                            />
                          }
                          label="TVT Minischlinge"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.optechnikAb2023InkopBulkingagent || false}
                              onChange={() => handleCheckboxChange("optechnikAb2023InkopBulkingagent")}
                            />
                          }
                          label="Bulking-Agent"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.optechnikAb2023InkopBotox || false}
                              onChange={() => handleCheckboxChange("optechnikAb2023InkopBotox")}
                            />
                          }
                          label="Botox"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.optechnikAb2024Burch || false}
                              onChange={() => handleCheckboxChange("optechnikAb2024Burch")}
                            />
                          }
                          label="BURCH"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.optechnikAb2024Weitere || false}
                              onChange={() => handleCheckboxChange("optechnikAb2024Weitere")}
                            />
                          }
                          label="weitere"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box sx={{ pt: 2 }}>
                      <Typography variant="h6" gutterBottom>
                        Robotik
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.optechnikAb2025Robotik || false}
                              onChange={() => handleCheckboxChange("optechnikAb2025Robotik")}
                            />
                          }
                          label="Robotik eingesetzt"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* Tab 4: OP-Material */}
              {activeTab === 3 && (
                <Box>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        OP-Material
                      </Typography>
                      <TextField
                        select
                        label="OP-Material"
                        value={formData.optechnikAb2025MaterialId ?? ""}
                        onChange={(e) =>
                          handleTextChange(
                            "optechnikAb2025MaterialId",
                            e.target.value === "" ? undefined : Number(e.target.value)
                          )
                        }
                        fullWidth
                      >
                        <MenuItem value="">(kein OP-Material)</MenuItem>
                        {materials.map((m) => (
                          <MenuItem key={m.id} value={m.id}>
                            {m.bezeichnung}
                            {m.materialherstellerBezeichnung ? ` (${m.materialherstellerBezeichnung})` : ""}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* Tab 5: OP-Komplikationen */}
              {activeTab === 4 && (
                <Box>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Operationskomplikationen
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.opkomplikationWundheilung || false}
                              onChange={() => handleCheckboxChange("opkomplikationWundheilung")}
                            />
                          }
                          label="Wundheilungsstörung"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.opkomplikationSchmerzsyndrom || false}
                              onChange={() => handleCheckboxChange("opkomplikationSchmerzsyndrom")}
                            />
                          }
                          label="Schmerzsyndrom"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.opkomplikationRevisionsop || false}
                              onChange={() => handleCheckboxChange("opkomplikationRevisionsop")}
                            />
                          }
                          label="Revisionsoperation"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.opkomplikationOrganverletzung || false}
                              onChange={() => handleCheckboxChange("opkomplikationOrganverletzung")}
                            />
                          }
                          label="Organverletzung"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.opkomplikationNahtdehiszenz || false}
                              onChange={() => handleCheckboxChange("opkomplikationNahtdehiszenz")}
                            />
                          }
                          label="Nahtdehiszenz"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* Tab 6: Kontrolle 3M */}
              {activeTab === 5 && (
                <Box>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Kontrolle Status
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mErschienen || false}
                              onChange={() => handleCheckboxChange("kontrolle3mErschienen")}
                            />
                          }
                          label="Patient erschienen"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mNeuerfalloderrezidiv || false}
                              onChange={() => handleCheckboxChange("kontrolle3mNeuerfalloderrezidiv")}
                            />
                          }
                          label="Neuer Fall oder Rezidiv"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mNichtmoeglichcorona || false}
                              onChange={() => handleCheckboxChange("kontrolle3mNichtmoeglichcorona")}
                            />
                          }
                          label="Nicht möglich (Corona)"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Ärztliche Kontrolle - Kontinenz
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mKontKontinent || false}
                              onChange={() => handleCheckboxChange("kontrolle3mKontKontinent")}
                            />
                          }
                          label="Kontinent"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mKontDeutlichgebessert || false}
                              onChange={() => handleCheckboxChange("kontrolle3mKontDeutlichgebessert")}
                            />
                          }
                          label="Deutlich gebessert"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mKontUnveraendert || false}
                              onChange={() => handleCheckboxChange("kontrolle3mKontUnveraendert")}
                            />
                          }
                          label="Unverändert"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mKontVerschlechtert || false}
                              onChange={() => handleCheckboxChange("kontrolle3mKontVerschlechtert")}
                            />
                          }
                          label="Verschlechtert"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Ärztliche Kontrolle - Senkung
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mSenkungErreicht || false}
                              onChange={() => handleCheckboxChange("kontrolle3mSenkungErreicht")}
                            />
                          }
                          label="Ziel erreicht"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mSenkungTeilweise || false}
                              onChange={() => handleCheckboxChange("kontrolle3mSenkungTeilweise")}
                            />
                          }
                          label="Teilweise erreicht"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mSenkungTeilrezidiv || false}
                              onChange={() => handleCheckboxChange("kontrolle3mSenkungTeilrezidiv")}
                            />
                          }
                          label="Teilrezidiv"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mSenkungVollrezidiv || false}
                              onChange={() => handleCheckboxChange("kontrolle3mSenkungVollrezidiv")}
                            />
                          }
                          label="Vollrezidiv"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Sekundärkomplikationen
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mSekkompl_wundheilung || false}
                              onChange={() => handleCheckboxChange("kontrolle3mSekkompl_wundheilung")}
                            />
                          }
                          label="Wundheilungsstörung"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mSekkompl_netzarrosion || false}
                              onChange={() => handleCheckboxChange("kontrolle3mSekkompl_netzarrosion")}
                            />
                          }
                          label="Netzarrosion"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mSekkompl_schmerzsyndrom || false}
                              onChange={() => handleCheckboxChange("kontrolle3mSekkompl_schmerzsyndrom")}
                            />
                          }
                          label="Schmerzsyndrom"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mSekkompl_revisionsop || false}
                              onChange={() => handleCheckboxChange("kontrolle3mSekkompl_revisionsop")}
                            />
                          }
                          label="Revisionsoperation"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Patientenzufriedenheit F1 - Haben sich Ihre Beschwerden durch die Operation gebessert?
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF1Ganz || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF1Ganz")}
                            />
                          }
                          label="Ganz gebessert / meine Beschwerden sind weg"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF1Teilweise || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF1Teilweise")}
                            />
                          }
                          label="Teilweise gebessert / ein Teil meiner Beschwerden ist weg"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF1Kurzfristig || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF1Kurzfristig")}
                            />
                          }
                          label="Nur kurzfristig gebessert / meine Beschwerden sind wieder zurück"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF1Garnicht || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF1Garnicht")}
                            />
                          }
                          label="Gar nicht gebessert / meine Beschwerden sind noch genauso wie vor der Operation"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Patientenzufriedenheit F2 - Haben Sie seit der Operation neue Beschwerden?
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF2Nein || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF2Nein")}
                            />
                          }
                          label="Nein, keine"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF2JaZiehen || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF2JaZiehen")}
                            />
                          }
                          label="Ja, Ziehen"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF2JaStechen || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF2JaStechen")}
                            />
                          }
                          label="Ja, Stechen"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF2JaFremdkoerper || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF2JaFremdkoerper")}
                            />
                          }
                          label="Ja, Fremdkörpergefühl"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF2JaWeitere || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF2JaWeitere")}
                            />
                          }
                          label="Ja, Weitere"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                      {formData.kontrolle3mPatzufF2JaWeitere && (
                        <TextField
                          label="Weitere Beschwerden (Text)"
                          value={formData.kontrolle3mPatzufF2JaWeitereText || ""}
                          onChange={(e) => handleTextChange("kontrolle3mPatzufF2JaWeitereText", e.target.value)}
                          fullWidth
                          sx={{ mt: 2 }}
                        />
                      )}
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Patientenzufriedenheit F3 - Ich würde mich jederzeit wieder aufgrund meiner Beschwerden operieren lassen
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF3Ganz || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF3Ganz")}
                            />
                          }
                          label="Stimme voll und ganz zu"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF3Teils || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF3Teils")}
                            />
                          }
                          label="Stimme eher zu"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF3Teilweise || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF3Teilweise")}
                            />
                          }
                          label="Teils, teils"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF3Ehernicht || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF3Ehernicht")}
                            />
                          }
                          label="Stimme eher nicht zu"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle3mPatzufF3Garnicht || false}
                              onChange={() => handleCheckboxChange("kontrolle3mPatzufF3Garnicht")}
                            />
                          }
                          label="Stimme gar nicht zu"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* Tab 7: Kontrolle 12M */}
              {activeTab === 6 && (
                <Box>
                  <Stack spacing={3}>
                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Kontrolle Status
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mErschienen || false}
                              onChange={() => handleCheckboxChange("kontrolle12mErschienen")}
                            />
                          }
                          label="Patient erschienen"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mNeuerfalloderrezidiv || false}
                              onChange={() => handleCheckboxChange("kontrolle12mNeuerfalloderrezidiv")}
                            />
                          }
                          label="Neuer Fall oder Rezidiv"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mNichtmoeglichcorona || false}
                              onChange={() => handleCheckboxChange("kontrolle12mNichtmoeglichcorona")}
                            />
                          }
                          label="Nicht möglich (Corona)"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Ärztliche Kontrolle - Kontinenz
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mKontKontinent || false}
                              onChange={() => handleCheckboxChange("kontrolle12mKontKontinent")}
                            />
                          }
                          label="Kontinent"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mKontDeutlichgebessert || false}
                              onChange={() => handleCheckboxChange("kontrolle12mKontDeutlichgebessert")}
                            />
                          }
                          label="Deutlich gebessert"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mKontUnveraendert || false}
                              onChange={() => handleCheckboxChange("kontrolle12mKontUnveraendert")}
                            />
                          }
                          label="Unverändert"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mKontVerschlechtert || false}
                              onChange={() => handleCheckboxChange("kontrolle12mKontVerschlechtert")}
                            />
                          }
                          label="Verschlechtert"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Ärztliche Kontrolle - Senkung
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mSenkungErreicht || false}
                              onChange={() => handleCheckboxChange("kontrolle12mSenkungErreicht")}
                            />
                          }
                          label="Ziel erreicht"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mSenkungTeilweise || false}
                              onChange={() => handleCheckboxChange("kontrolle12mSenkungTeilweise")}
                            />
                          }
                          label="Teilweise erreicht"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mSenkungTeilrezidiv || false}
                              onChange={() => handleCheckboxChange("kontrolle12mSenkungTeilrezidiv")}
                            />
                          }
                          label="Teilrezidiv"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mSenkungVollrezidiv || false}
                              onChange={() => handleCheckboxChange("kontrolle12mSenkungVollrezidiv")}
                            />
                          }
                          label="Vollrezidiv"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Sekundärkomplikationen
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mSekkompl_wundheilung || false}
                              onChange={() => handleCheckboxChange("kontrolle12mSekkompl_wundheilung")}
                            />
                          }
                          label="Wundheilungsstörung"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mSekkompl_netzarrosion || false}
                              onChange={() => handleCheckboxChange("kontrolle12mSekkompl_netzarrosion")}
                            />
                          }
                          label="Netzarrosion"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mSekkompl_schmerzsyndrom || false}
                              onChange={() => handleCheckboxChange("kontrolle12mSekkompl_schmerzsyndrom")}
                            />
                          }
                          label="Schmerzsyndrom"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mSekkompl_revisionsop || false}
                              onChange={() => handleCheckboxChange("kontrolle12mSekkompl_revisionsop")}
                            />
                          }
                          label="Revisionsoperation"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Patientenzufriedenheit F1 - Haben sich Ihre Beschwerden durch die Operation gebessert?
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF1Ganz || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF1Ganz")}
                            />
                          }
                          label="Ganz gebessert / meine Beschwerden sind weg"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF1Teilweise || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF1Teilweise")}
                            />
                          }
                          label="Teilweise gebessert / ein Teil meiner Beschwerden ist weg"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF1Kurzfristig || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF1Kurzfristig")}
                            />
                          }
                          label="Nur kurzfristig gebessert / meine Beschwerden sind wieder zurück"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF1Garnicht || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF1Garnicht")}
                            />
                          }
                          label="Gar nicht gebessert / meine Beschwerden sind noch genauso wie vor der Operation"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Patientenzufriedenheit F2 - Haben Sie seit der Operation neue Beschwerden?
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF2Nein || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF2Nein")}
                            />
                          }
                          label="Nein, keine"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF2JaZiehen || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF2JaZiehen")}
                            />
                          }
                          label="Ja, Ziehen"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF2JaStechen || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF2JaStechen")}
                            />
                          }
                          label="Ja, Stechen"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF2JaFremdkoerper || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF2JaFremdkoerper")}
                            />
                          }
                          label="Ja, Fremdkörpergefühl"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF2JaWeitere || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF2JaWeitere")}
                            />
                          }
                          label="Ja, Weitere"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                      {formData.kontrolle12mPatzufF2JaWeitere && (
                        <TextField
                          label="Weitere Beschwerden (Text)"
                          value={formData.kontrolle12mPatzufF2JaWeitereText || ""}
                          onChange={(e) => handleTextChange("kontrolle12mPatzufF2JaWeitereText", e.target.value)}
                          fullWidth
                          sx={{ mt: 2 }}
                        />
                      )}
                    </Box>

                    <Divider />

                    <Box>
                      <Typography variant="h6" gutterBottom>
                        Patientenzufriedenheit F3 - Ich würde mich jederzeit wieder aufgrund meiner Beschwerden operieren lassen
                      </Typography>
                      <FormGroup row sx={checkboxGroupSx}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF3Ganz || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF3Ganz")}
                            />
                          }
                          label="Stimme voll und ganz zu"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF3Teils || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF3Teils")}
                            />
                          }
                          label="Stimme eher zu"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF3Teilweise || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF3Teilweise")}
                            />
                          }
                          label="Teils, teils"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF3Ehernicht || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF3Ehernicht")}
                            />
                          }
                          label="Stimme eher nicht zu"
                          sx={checkboxItemSx}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.kontrolle12mPatzufF3Garnicht || false}
                              onChange={() => handleCheckboxChange("kontrolle12mPatzufF3Garnicht")}
                            />
                          }
                          label="Stimme gar nicht zu"
                          sx={checkboxItemSx}
                        />
                      </FormGroup>
                    </Box>
                  </Stack>
                </Box>
              )}

              {/* Tab 8: Fehler / Hinweise */}
              {hasErrorsOrHints && activeTab === 7 && (
                <Box>
                  <PatientFehlerTab patientId={patientId} />
                </Box>
              )}
            </Box>

            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                pt: 2,
                borderTop: 1,
                borderColor: "divider",
                flexShrink: 0,
              }}
            >
              <Button onClick={onClose} variant="outlined">
                Abbrechen
              </Button>
              <Button type="submit" variant="contained" startIcon={<SaveIcon />} sx={{ color: "white" }}>
                Speichern
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default PatientEditModal2024;
